import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type {
	Editor as TinymceEditor,
	Bookmark as TinymceEditorBookmark,
	RawEditorSettings as TinymceSettings,
} from 'tinymce';
import tinymce from 'tinymce';

import type { PopupData, Service } from 'webapp-types';
import { Direction, PopupEvent } from 'webapp/enums/enums';
import { sanitize } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

import type InfolayerItem from 'webapp/models-custom/infolayer-item/general';
import type PopupList from 'webapp/models-custom/popup/popup-list';

type TinymceToolbarMode = TinymceSettings['toolbar_mode'];

// tinymce settings, plus our own options (onSetup and centerByDefault are removed before tinymce gets them)
export type HtmlEditorOptions = TinymceSettings & {
	sanitizeFn?: (html: string) => string;
	onSetup?: (component: FormHtmlEditorComponent, editor: TinymceEditor) => void;
	centerByDefault?: boolean;
	isInputFocused?: boolean;
	isDistractionFree?: boolean;
	commandPopupList?: PopupData.Common.List;
};

interface Args {
	text: string | null | undefined;
	onChange: (text: string) => void;
	onFocusOut?: (text: string) => void;
	maxChars?: number;
	options?: HtmlEditorOptions;
	static?: boolean;
}

export default class FormHtmlEditorComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('data-story') declare dataStoryService: Service.DataStory;
	@service('popup') declare popupService: Service.Popup;

	editor: TinymceEditor | null = null;
	infolayerItemForCharLimitError: InfolayerItem | null = null;
	// used to open command popup in new empty line
	private newLinePopupTimeoutId: number | null = null;

	initialHtml = '';

	// scroll listeners for toolbar repositioning
	private scrollListeners: { element: Element | Document; handler: EventListener }[] = [];

	private commandPopup: PopupList | null = null;
	private tempCommandPopupElement: HTMLElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		let sanitizeFn = sanitize;
		if (this.args.options?.sanitizeFn) {
			sanitizeFn = this.args.options.sanitizeFn;
			delete this.args.options.sanitizeFn;
		}

		const text = sanitizeFn(this.args.text || '');
		this.initialHtml = this.dataStoryService.wrapStoryElementsWithSpans(text);

		// to request minfied theme / skin etc. assets from public folder
		window.tinymce.suffix = '.min';
	}

	get isStatic() {
		return !!this.args.static;
	}

	get charLimitActive(): boolean {
		return !!this.args.maxChars;
	}

	@cached
	get editorMaxWidth(): number | undefined {
		if (!this.isStatic) {
			return;
		}

		return (
			this.editor?.getElement()?.closest<HTMLElement>('.user-html')?.offsetWidth || undefined
		);
	}

	get isDistractionFree(): boolean {
		return !!this.args.options?.isDistractionFree;
	}

	get hasSlashCommandPopup(): boolean {
		return !!this.args.options?.commandPopupList;
	}

	get canOpenSlashCommandPopup(): boolean {
		return this.isDistractionFree && this.hasSlashCommandPopup;
	}

	checkCharLimit(charCount: number): boolean {
		// returns true if all good, and false if we have exceeded limit
		const maxChars = this.args.maxChars,
			exceedsMaximum = maxChars && maxChars < charCount;
		if (exceedsMaximum) {
			const infolayerItem = this.infolayerItemForCharLimitError;
			if (!infolayerItem || infolayerItem.hidden) {
				const errorText = `Text too long, please limit it to ${maxChars} characters.`;
				this.infolayerItemForCharLimitError = this.infolayerService.error(errorText);
			}
		}
		return !exceedsMaximum;
	}

	@action
	setupView(node: HTMLElement): void {
		const extraOptions = this.args.options,
			onSetup = extraOptions?.onSetup, // custom setup logic
			centerByDefault = extraOptions?.centerByDefault,
			isInputFocused = extraOptions?.isInputFocused;
		const skippedColorsNames = ['Maroon red', 'Pine green', 'Grey', 'Black'];
		const color_map: string[] = [];
		this.constantsService.boardColors.forEach((boardColor) => {
			if (!skippedColorsNames.includes(boardColor.name)) {
				color_map.push(boardColor.hex, boardColor.name);
			}
		});

		let toolbar_mode: TinymceToolbarMode = 'floating';
		if (this.isStatic || window.innerWidth < 1280) {
			toolbar_mode = 'wrap';
		}

		let options: TinymceSettings = {
			target: node,
			base_url: '/lib/tinymce',
			inline: true,
			menubar: false,
			default_link_target: '_blank',
			link_default_protocol: 'https',
			font_formats:
				'Open Sans=Open Sans, sans-serif; Roboto=Roboto, sans-serif; Roboto Condensed=Roboto Condensed, sans-serif; Fira Sans=Fira Sans, sans-serif; Nunito=Nunito, sans-serif; Roboto Slab=Roboto Slab, serif; EB Garamond=EB Garamond, serif; Merriweather=Merriweather, serif; Gelasio=Gelasio, serif; Playfair Display=Playfair Display, serif;  Spline Sans Mono=Spline Sans Mono, monospace; Courier Prime=Courier Prime, monospace; Caveat=Caveat, cursive; Lobster=Lobster, sans-serif;',
			// Add custom colors: https://www.tiny.cloud/docs/configure/content-appearance/#color_map
			color_map, // filtered colors according to `skippedColorsNames`; also we hide #E71515 with css (reports.less - search by hex value) - since #000000 and #E71515 are predefined and no setting to hide this color was found
			custom_colors: false,
			relative_urls: false,
			remove_script_host: false,
			style_formats: [
				{ title: 'Heading', block: 'h3' },
				{ title: 'Heading 2', block: 'h4' },
				{ title: 'Heading 3', block: 'h5' },
				{ title: 'Paragraph large', block: 'p', attributes: { class: 'large' } },
				{ title: 'Paragraph', block: 'p', attributes: { class: 'normal' } },
				{ title: 'Paragraph small', block: 'p', attributes: { class: 'small' } },
				{ title: 'Caption', block: 'p', attributes: { class: 'caption' } },
			],
			indentation: '1em',
			toolbar_mode,
			setup: (editor) => {
				this.editor = editor;
				const editorElement = editor.getElement();

				editor.on('init', () => {
					if (centerByDefault) {
						const container = editor.getContentAreaContainer();

						if (container) {
							container.classList.add('placeholder-center');
						}
					}

					if (isInputFocused) {
						editor.focus();
					}

					// add scroll event listener for all containers which could reposition toolbars
					const scrollableParents = [
						document,
						editorElement.closest('#report-editor_body'),
						editorElement.closest('.usercontent'),
						editorElement.closest('.user-html'),
						editorElement.closest('.fragment--story-text'),
					].filter(Boolean);

					scrollableParents.forEach((element) => {
						if (element) {
							const handler = () => {
								if (editor.hasFocus()) {
									this.repositionAllToxTinymceToolbars(editor);
								}

								this.repositionCommandPopup();
							};

							element.addEventListener('scroll', handler, { passive: true });

							this.scrollListeners.push({ element, handler });
						}
					});
				});

				if (this.canOpenSlashCommandPopup) {
					editor.on('keydown', (e: KeyboardEvent) => {
						// if slash is typed, open the popup
						if (e.key === '/') {
							// without next this does not work when inserting in new line because there is no selection
							next(() => {
								this.openSlashCommandPopup(editor);
							});
							return true;
						}

						// if slash popup is open and user types any character close the popup
						if (this.commandPopup) {
							void this.commandPopup.close();
						}

						return true;
					});
				}

				editor.on('Change', () => {
					const content = editor.getContent();
					this.args.onChange(content);
				});

				editor.on('blur', () => {
					const content = editor.getContent();
					this.args.onFocusOut?.(content);
				});

				// scroll to focus when pressing multiple consecutive Enter: https://stackoverflow.com/a/48853592
				editor.on('keyup', (e: KeyboardEvent) => {
					if (e.key === 'Enter') {
						const isPageless = editorElement.closest('.pageless-wrapper');
						if (!isPageless) {
							editor.selection.getNode().scrollIntoView(false);
						}

						// when user enters new line, open command popup
						this.setupNewLinePopupTimeout(editor);
					} else if (this.newLinePopupTimeoutId !== null) {
						// clear open popup timeout if user types anything
						this.clearNewLinePopupTimeout();
					}

					if (!this.isDistractionFree) {
						debounce(this, this.repositionToolbarToTop, editor, 500);
					}
				});

				editor.on('focus', () => {
					const isEmpty = !this.args.text;
					if (isEmpty) {
						next(() => {
							editor.execCommand('fontName', false, 'Roboto');

							if (centerByDefault) {
								const state = editor.queryCommandState('JustifyCenter');
								if (!state) {
									editor.execCommand('JustifyCenter');
								}
							}
						});
					}

					this.repositionToolbarToTop(editor);
					this.setEditorToolbarWidth();
				});

				// Used because of custom HTML element (story-element)
				const eventsToPreserveSelection = [
					'FontName',
					'mceApplyTextcolor',
					'mceToggleFormat',
					'JustifyLeft',
					'JustifyCenter',
					'JustifyRight',
				];
				let selection: TinymceEditorBookmark | null = null;
				editor.on('BeforeExecCommand', (e) => {
					const content = editor.selection.getContent();
					if (
						content &&
						content.includes('story-element') &&
						eventsToPreserveSelection.includes(e.command)
					) {
						selection = editor.selection.getBookmark(); // Save current selection
					}
				});

				editor.on('ExecCommand', (e) => {
					const content = editor.selection.getContent();
					if (
						content &&
						content.includes('story-element') &&
						eventsToPreserveSelection.includes(e.command)
					) {
						if (selection) {
							editor.selection.moveToBookmark(selection); // Restore the saved selection range
							selection = null;
						}
					}
				});

				if (this.charLimitActive) {
					editor.on('keydown', (event) => {
						const isKeyAlwaysAllowed = event.keyCode < 48; // function keys, delete keys etc.
						if (!isKeyAlwaysAllowed) {
							// tinymce types its plugins as any
							const wordCountPlugin = editor.plugins.wordcount as
								{ body: { getCharacterCount(): number } } | undefined;
							if (wordCountPlugin) {
								const charCount = wordCountPlugin.body.getCharacterCount(),
									charLimitValid = this.checkCharLimit(charCount);
								if (!charLimitValid) {
									event.preventDefault();
									event.stopPropagation();
									return false;
								}
							}
						}
						return true;
					});

					editor.on('paste', (event) => {
						const data = event.clipboardData?.getData('text') || '';
						return this.checkCharLimit(data.length);
					});
				}

				if (onSetup) {
					onSetup(this, editor);
				}
			},
		};

		if (extraOptions) {
			delete extraOptions.onSetup;
			delete extraOptions.centerByDefault;
			options = Object.assign(options, extraOptions);
		}

		void tinymce.init(options);
	}

	private repositionToolbarToTop(editor: TinymceEditor) {
		if (this.isStatic) {
			return;
		}

		// Make toolbar fixed to the top of the `user-html` element
		// (also while scrolling - https://app.asana.com/0/1180537054235286/1207824343050321/f)
		// `fixed_toolbar_container_target` problem with this (https://app.asana.com/0/1138028810573038/1208129609342584/f)
		const userHtmlNode = editor.getElement().closest('.user-html')!;
		if (!userHtmlNode) {
			return;
		}

		const toolbarContainerPosY = userHtmlNode.getBoundingClientRect().top;
		const scrollableContentNode =
			editor.getElement().closest('.usercontent') || editor.getElement().closest('.block');
		const scrollableContentHeight = (scrollableContentNode as HTMLElement)?.scrollTop || 0;

		next(() => {
			const toolbarNode = editor.getContainer();
			const toolbarHeight = toolbarNode.offsetHeight || 40;
			const toolbarExtraOffset = 10;

			toolbarNode.style.top = `${toolbarContainerPosY - toolbarHeight - toolbarExtraOffset + scrollableContentHeight}px`;
		});
	}

	private setEditorToolbarWidth() {
		if (!this.editorMaxWidth) {
			return;
		}

		next(() => {
			const toxEditorElements = document.querySelectorAll('.tox-editor-header, .tox-toolbar');
			const elements = [...toxEditorElements];

			elements.forEach((element) => {
				if (element instanceof HTMLElement) {
					// Set width to prevent auto-shrinking or extending of the HTML editor in the designer's right panel
					element.style.setProperty('min-width', `${this.editorMaxWidth}px`);
					element.style.setProperty('max-width', `${this.editorMaxWidth}px`, 'important');
				}
			});
		});
	}

	@action
	teardownView(): void {
		this.clearNewLinePopupTimeout();

		this.scrollListeners.forEach(({ element, handler }) => {
			element.removeEventListener('scroll', handler);
		});
		this.scrollListeners = [];

		this.removeCommandPopupListeners();
		this.clearTempCommandPopupElement();

		const editor = this.editor;
		if (editor) {
			editor.destroy();
		}
	}

	//--- COMMAND POPUP START ---//

	private openSlashCommandPopup(editor: TinymceEditor): void {
		if (this.commandPopup || !this.canOpenSlashCommandPopup) {
			return;
		}

		const cursorPosition = this.getCursorPosition(editor);

		if (!cursorPosition) {
			return;
		}

		// create a temporary element at cursor position to attach the popup to
		this.tempCommandPopupElement = document.createElement('div');
		this.tempCommandPopupElement.style.position = 'absolute';
		this.tempCommandPopupElement.style.left = `${cursorPosition.left}px`;
		this.tempCommandPopupElement.style.top = `${cursorPosition.top}px`;
		this.tempCommandPopupElement.style.height = '1px';
		this.tempCommandPopupElement.style.width = '1px';
		document.body.appendChild(this.tempCommandPopupElement);

		const popup = this.popupService.createListPopup(
			this.tempCommandPopupElement,
			'',
			'popup-slash-commands',
			true,
			true,
		);

		if (popup) {
			this.commandPopup = popup;
			this.setupCommandPopupListeners();

			popup.directions = [Direction.DOWN, Direction.RIGHT, Direction.UP, Direction.LEFT];
			popup.offset = 20;
			popup.open(this.args.options!.commandPopupList);
			popup.on(PopupEvent.CLOSED, this, () => {
				this.commandPopup = null;
				this.clearTempCommandPopupElement();
				this.removeCommandPopupListeners();
			});
		} else {
			this.clearTempCommandPopupElement();
		}
	}

	@action
	private repositionCommandPopup(): void {
		if (!this.tempCommandPopupElement || !this.editor || !this.commandPopup) {
			return;
		}

		// get new cursor position
		const newPosition = this.getCursorPosition(this.editor);
		if (!newPosition) {
			return;
		}

		// update temp element position and reposition the actual popup
		this.tempCommandPopupElement.style.left = `${newPosition.left}px`;
		this.tempCommandPopupElement.style.top = `${newPosition.top}px`;
		this.commandPopup.reposition();
	}

	private getCursorPosition(editor: TinymceEditor): { left: number; top: number } | null {
		// first try to get position from selection
		const selection = editor.selection.getSel();
		const range = selection?.getRangeAt(0);

		if (range) {
			const rect = range.getBoundingClientRect();
			// Check if we got valid coordinates
			if (rect.left !== 0 || rect.top !== 0) {
				return {
					left: rect.left,
					top: rect.top,
				};
			}
		}

		// fallback for cases when selection doesn't give valid coordinates (e.g., after Enter)
		// if "Enter" is pressed, the selection is lost and the cursor position is not found (0 is always returned for left and top)
		// marker has to be created for the editor.selection.getRng() to work in this case
		const markerId = 'cursor-position-marker';
		const bookmark = editor.selection.getBookmark();

		// insert temporary marker and get its position
		editor.undoManager.transact(() => {
			editor.selection.setContent(`<span id="${markerId}">\u200b</span>`);
		});

		const markerElement = editor.getDoc().getElementById(markerId);
		if (!markerElement) {
			return null;
		}

		const { left, top } = markerElement.getBoundingClientRect();

		// cleanup
		editor.undoManager.transact(() => {
			markerElement.remove();
			editor.selection.moveToBookmark(bookmark);
		});

		return { left, top };
	}

	private repositionAllToxTinymceToolbars(editor: TinymceEditor): void {
		if (!this.isDistractionFree) {
			this.repositionToolbarToTop(editor);
		}

		const quickbars = document.querySelectorAll('.tox-tinymce-aux');
		if (quickbars.length > 0) {
			// TinyMCE handles quickbar repositioning internally with ResizeEditor
			editor.fire('ResizeEditor');
		}
	}

	private setupNewLinePopupTimeout(editor: TinymceEditor): void {
		// clear any existing timeout
		this.clearNewLinePopupTimeout();

		// set a new timeout to open command popup in empty line
		if (this.canOpenSlashCommandPopup) {
			this.newLinePopupTimeoutId = window.setTimeout(() => {
				const node = editor.selection.getNode();
				const isEmpty = node.textContent?.trim() === '';

				if (isEmpty) {
					this.openSlashCommandPopup(editor);
				}
				this.newLinePopupTimeoutId = null;
			}, 2000);
		}
	}

	private clearNewLinePopupTimeout(): void {
		if (this.newLinePopupTimeoutId !== null) {
			window.clearTimeout(this.newLinePopupTimeoutId);
			this.newLinePopupTimeoutId = null;
		}
	}

	private clearTempCommandPopupElement(): void {
		if (this.tempCommandPopupElement) {
			document.body.removeChild(this.tempCommandPopupElement);
			this.tempCommandPopupElement = null;
		}
	}

	private setupCommandPopupListeners(): void {
		window.addEventListener('resize', this.repositionCommandPopup);
		document.addEventListener('scroll', this.repositionCommandPopup, true);
	}

	private removeCommandPopupListeners(): void {
		window.removeEventListener('resize', this.repositionCommandPopup);
		document.removeEventListener('scroll', this.repositionCommandPopup, true);
	}

	//--- COMMAND POPUP END ---//
	<template>
		{{! one-liner to skip the unbreaking spaces generated }}
		<div
			class="html-editor-target"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			{{{this.initialHtml}}}
		</div>
	</template>
}
